import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getSuggestions } from '@/utils/search';
import { useTheme } from '@/hooks/useTheme';
import DarkModeToggle from '@/components/base/DarkModeToggle';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    setSuggestions(getSuggestions(val));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    setSearchOpen(false);
    setSearchQuery('');
    setSuggestions([]);
  };

  const solidBg = isDark ? 'bg-stone-900/95 backdrop-blur-sm border-b border-stone-800' : 'bg-white/95 backdrop-blur-sm shadow-sm';
  // Always show solid background — NicheBanner sits above the hero so transparent nav is never over a dark image
  const navBg = solidBg;
  const textColor = isDark ? 'text-stone-200' : 'text-stone-800';
  const logoColor = 'text-rose-500';
  const iconColor = isDark ? 'text-stone-400 hover:text-rose-400' : 'text-stone-600 hover:text-rose-500';

  const dropdownBg = isDark ? 'bg-stone-800 border-stone-700' : 'bg-white border-stone-200';
  const dropdownText = isDark ? 'text-stone-200' : 'text-stone-800';
  const dropdownMuted = isDark ? 'text-stone-400' : 'text-stone-400';
  const dropdownHover = isDark ? 'hover:bg-stone-700 hover:text-rose-400' : 'hover:bg-rose-50 hover:text-rose-600';
  const chipBg = isDark ? 'bg-stone-700 text-stone-300 hover:bg-stone-600' : 'bg-stone-100 text-stone-600 hover:bg-rose-50 hover:text-rose-600';
  const mobileMenuBg = isDark ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100';
  const mobileSearchBg = isDark ? 'bg-stone-800 border-stone-700' : 'bg-stone-50 border-stone-200';
  const mobileSearchText = isDark ? 'text-stone-200 placeholder-stone-500' : 'text-stone-800 placeholder-stone-400';
  const mobileLinkColor = isDark ? 'text-stone-300 hover:text-rose-400' : 'text-stone-700 hover:text-rose-500';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0">
            <span
              className={`text-2xl font-bold tracking-tight ${logoColor} transition-colors duration-300`}
              style={{ fontFamily: "'Teko', sans-serif", letterSpacing: '0.02em' }}
            >
              roameurope<span className="text-rose-400">.blog</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-5 flex-1 justify-center">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`text-sm font-medium transition-colors duration-200 hover:text-rose-500 whitespace-nowrap cursor-pointer ${textColor}`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/archive"
              className={`text-sm font-medium transition-colors duration-200 hover:text-rose-500 whitespace-nowrap cursor-pointer ${textColor}`}
            >
              All Articles
            </Link>
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors duration-200 hover:text-rose-500 whitespace-nowrap cursor-pointer ${textColor}`}
            >
              About
            </Link>
            <Link
              to="/search"
              className={`text-sm font-medium transition-colors duration-200 hover:text-rose-500 whitespace-nowrap cursor-pointer flex items-center gap-1 ${textColor}`}
            >
              <i className="ri-search-line text-xs" />
              Search
            </Link>
          </nav>

          {/* Right: dark toggle + search + hamburger */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <DarkModeToggle className={iconColor} />

            {/* Search toggle */}
            <div ref={searchRef} className="relative">
              <button
                onClick={() => { setSearchOpen(!searchOpen); setSearchQuery(''); setSuggestions([]); }}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${iconColor}`}
                aria-label="Search"
              >
                <i className={`text-lg ${searchOpen ? 'ri-close-line' : 'ri-search-line'}`} />
              </button>

              {/* Search dropdown */}
              {searchOpen && (
                <div className={`absolute right-0 top-12 w-80 rounded-xl border overflow-hidden z-50 ${dropdownBg}`}>
                  <form onSubmit={handleSearchSubmit} className={`flex items-center px-3 py-2 border-b ${isDark ? 'border-stone-700' : 'border-stone-100'}`}>
                    <div className={`w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0 ${dropdownMuted}`}>
                      <i className="ri-search-line" />
                    </div>
                    <input
                      ref={inputRef}
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchInput}
                      placeholder="Search articles, tags..."
                      className={`flex-1 text-sm bg-transparent outline-none placeholder-stone-400 py-1 ${dropdownText}`}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => { setSearchQuery(''); setSuggestions([]); inputRef.current?.focus(); }}
                        className={`w-5 h-5 flex items-center justify-center cursor-pointer ml-1 ${dropdownMuted} hover:text-stone-300`}
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    )}
                  </form>

                  {suggestions.length > 0 && (
                    <ul className="py-1 max-h-64 overflow-y-auto">
                      {suggestions.map((s) => (
                        <li key={s}>
                          <button
                            onClick={() => handleSuggestionClick(s)}
                            className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 cursor-pointer transition-colors ${dropdownText} ${dropdownHover}`}
                          >
                            <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${dropdownMuted}`}>
                              <i className="ri-arrow-right-up-line text-xs" />
                            </div>
                            <span className="truncate">{s}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {!searchQuery && (
                    <div className="px-4 py-3">
                      <p className={`text-xs mb-2 ${dropdownMuted}`}>Popular searches</p>
                      <div className="flex flex-wrap gap-1.5">
                        {['love languages', 'breakup', 'boundaries', 'attachment', 'first date'].map((term) => (
                          <button
                            key={term}
                            onClick={() => handleSuggestionClick(term)}
                            className={`px-2.5 py-1 rounded-full text-xs cursor-pointer transition-colors whitespace-nowrap ${chipBg}`}
                          >
                            {term}
                          </button>
                        ))}
                      </div>
                      <Link
                        to="/search"
                        onClick={() => setSearchOpen(false)}
                        className="mt-3 flex items-center gap-1 text-xs text-rose-500 hover:text-rose-400 font-medium cursor-pointer"
                      >
                        <i className="ri-search-2-line" /> Browse all articles
                      </Link>
                    </div>
                  )}

                  {searchQuery && suggestions.length === 0 && (
                    <div className="px-4 py-3 text-center">
                      <p className={`text-xs mb-2 ${dropdownMuted}`}>No suggestions found</p>
                      <button
                        onClick={handleSearchSubmit as unknown as React.MouseEventHandler}
                        className="text-xs text-rose-500 hover:text-rose-400 font-medium cursor-pointer"
                      >
                        Search for &ldquo;{searchQuery}&rdquo; →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${iconColor}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className={`md:hidden border-t px-4 py-4 flex flex-col gap-3 ${mobileMenuBg}`}>
          <form
            onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) { navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`); setMenuOpen(false); setSearchQuery(''); } }}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 border mb-1 ${mobileSearchBg}`}
          >
            <div className={`w-4 h-4 flex items-center justify-center ${dropdownMuted}`}>
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles..."
              className={`flex-1 text-sm bg-transparent outline-none ${mobileSearchText}`}
            />
          </form>

          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`text-sm font-medium cursor-pointer py-1 ${mobileLinkColor}`}
              onClick={() => setMenuOpen(false)}
            >
              {cat.name}
            </Link>
          ))}

          <Link
            to="/archive"
            className={`text-sm font-medium cursor-pointer py-1 flex items-center gap-1 ${mobileLinkColor}`}
            onClick={() => setMenuOpen(false)}
          >
            <i className="ri-archive-line" /> All Articles
          </Link>
          <Link
            to="/about"
            className={`text-sm font-medium cursor-pointer py-1 flex items-center gap-1 ${mobileLinkColor}`}
            onClick={() => setMenuOpen(false)}
          >
            <i className="ri-team-line" /> About Us
          </Link>

          <Link
            to="/search"
            className="text-sm font-medium text-rose-500 hover:text-rose-400 cursor-pointer py-1 flex items-center gap-1"
            onClick={() => setMenuOpen(false)}
          >
            <i className="ri-search-2-line" /> Search articles
          </Link>
        </div>
      )}
    </header>
  );
}
